import {
  ApplicationTypeKey,
  FilterCategoryKey,
  FrameworkKey,
  ProgrammingLanguageKey,
  TopicKey,
} from "@hub/data/values/data-keys.enum";
import { FilterOptions } from "@hub/models/ui/filter-options";
import { DocumentFilterGroups } from "@hub/models/filters/document-filter-groups";
import { DocumentCardData } from "@hub/models/document/document-card-data";
import { ContentFilteringService } from "@hub/services/filtering/content-filtering.service";
import {
  getRouterQueryWithSelectedFilters,
  loadFiltersOnUrl,
} from "@hub/services/filtering/content-filtering.utils";
import { MetadataService } from "@hub/services/metadata.service";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

interface FilterValues {
  [index: string]: string[] | undefined;
  topic?: string[];
  type: string[];
  framework: string[];
  language: string[];
}

export const useContentFiltering = () => {
  const [results, setResults] = useState<DocumentCardData[] | null>(null);
  const [hasSelectedFilters, setHasSelectedFilters] = useState<boolean>(false);

  const [typeFilterValues, setTypeFilterValues] = useState<string[]>([]);
  const [frameworkFilterValues, setFrameworkFilterValues] = useState<string[]>(
    []
  );
  const [languageFilterValues, setLanguageFilterValues] = useState<string[]>(
    []
  );
  const [featureFilterValues, setFeatureFilterValues] = useState<string[]>([]);
  const [verticalFilterValues, setVerticalFilterValues] = useState<string[]>(
    []
  );
  const [topicFilterValues, setTopicFilterValues] = useState<string[]>([]);
  const [searchTermFilterValue, setSearchTermFilterValue] =
    useState<string>("");

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const router = useRouter();

  const [filterOptions, setFilterOptions] =
    useState<DocumentFilterGroups | null>(null);

  useEffect(() => {
    const filterOptionsSub = ContentFilteringService.filterOptions$.subscribe(
      (data) => {
        setFilterOptions(data);
      }
    );

    return () => {
      if (filterOptionsSub) {
        filterOptionsSub.unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    const isModalVisibleSub = ContentFilteringService.isModalVisible$.subscribe(
      (data) => {
        setIsModalVisible(data);
      }
    );

    return () => {
      if (isModalVisibleSub) {
        isModalVisibleSub.unsubscribe();
      }
    };
  }, []);

  const clearAllFilterValues = () => {
    ContentFilteringService.clearAllFilterValues();
  };

  useEffect(() => {
    const typeFilterValuesSub =
      ContentFilteringService.typeFilterValues$.subscribe((data) => {
        setTypeFilterValues(data);
      });

    const frameworkFilterValues =
      ContentFilteringService.frameworkFilterValues$.subscribe((data) => {
        setFrameworkFilterValues(data);
      });

    const languageFilterValueSub =
      ContentFilteringService.languageFilterValues$.subscribe((data) => {
        setLanguageFilterValues(data);
      });

    const featureFilterValuesSub =
      ContentFilteringService.featureFilterValues$.subscribe((data) => {
        setFeatureFilterValues(data);
      });

    const verticalFilterValuesSub =
      ContentFilteringService.verticalFilterValues$.subscribe((data) => {
        setVerticalFilterValues(data);
      });

    const topicFilterValuesSub =
      ContentFilteringService.topicFilterValues$.subscribe((data) => {
        setTopicFilterValues(data);
      });

    const searchTermFilterValueSub =
      ContentFilteringService.searchTermFilterValue$.subscribe((data) => {
        setSearchTermFilterValue(data);
      });

    const resultsSub = ContentFilteringService.results$.subscribe((data) => {
      setResults(data);
    });

    const hasSelectedFiltersSub =
      ContentFilteringService.hasSelectedFilters$.subscribe((data) => {
        setHasSelectedFilters(data);
      });

    return () => {
      if (typeFilterValuesSub) {
        typeFilterValuesSub.unsubscribe();
      }

      if (frameworkFilterValues) {
        frameworkFilterValues.unsubscribe();
      }

      if (languageFilterValueSub) {
        languageFilterValueSub.unsubscribe();
      }

      if (featureFilterValuesSub) {
        featureFilterValuesSub.unsubscribe();
      }

      if (verticalFilterValuesSub) {
        verticalFilterValuesSub.unsubscribe();
      }

      if (topicFilterValuesSub) {
        topicFilterValuesSub.unsubscribe();
      }

      if (searchTermFilterValueSub) {
        searchTermFilterValueSub.unsubscribe();
      }

      if (resultsSub) {
        resultsSub.unsubscribe();
      }

      if (hasSelectedFiltersSub) {
        hasSelectedFiltersSub.unsubscribe();
      }
    };
  }, []);

  const filterValues: FilterValues = {
    topic: topicFilterValues,
    type: [...typeFilterValues],
    framework: [...frameworkFilterValues],
    language: [...languageFilterValues],
  };

  const availableFilterOptions: FilterOptions = {
    topicOptions: [],
    typeOptions: [],
    frameworkOptions: [],
    DocumentPickerLanguageOptions: [],
  };

  if (filterOptions) {
    const {
      frameworkOptions,
      typeOptions,
      DocumentPickerLanguageOptions,
      topicOptions,
    } = filterOptions;

    if (typeOptions && typeOptions.length > 0) {
      for (const typeElement of typeOptions) {
        const displayName = MetadataService.getApplicationTypeDisplayName(
          typeElement as ApplicationTypeKey
        );

        if (!displayName) {
          continue;
        }

        availableFilterOptions.typeOptions.push({
          value: typeElement as ApplicationTypeKey,
          displayName,
        });
      }
    }

    if (topicOptions) {
      for (const topicElement of topicOptions) {
        const displayName = MetadataService.getTopicDisplayName(
          topicElement as TopicKey
        );

        if (!displayName) {
          continue;
        }

        availableFilterOptions.topicOptions.push({
          value: topicElement as TopicKey,
          displayName,
          logo: "",
        });
      }
    }

    if (DocumentPickerLanguageOptions) {
      for (const languageElement of DocumentPickerLanguageOptions) {
        const displayName = MetadataService.getProgrammingLanguageDisplayName(
          languageElement as ProgrammingLanguageKey
        );

        const logo = MetadataService.getProgrammingLanguageIcon(
          languageElement as ProgrammingLanguageKey
        );

        const extension = MetadataService.getProgrammingLanguageExtension(
          languageElement as ProgrammingLanguageKey
        );

        if (!(logo && displayName && extension)) {
          continue;
        }

        availableFilterOptions.DocumentPickerLanguageOptions.push({
          value: languageElement as ProgrammingLanguageKey,
          displayName,
          extension,
          logo,
        });
      }
    }

    if (frameworkOptions) {
      for (const frameworkElement of frameworkOptions) {
        const displayName = MetadataService.getFrameworkDisplayName(
          frameworkElement as FrameworkKey
        );

        const logo = MetadataService.getFrameworkIcon(
          frameworkElement as FrameworkKey
        );

        if (!(logo && displayName)) {
          continue;
        }

        availableFilterOptions.frameworkOptions.push({
          value: frameworkElement as FrameworkKey,
          displayName,
          logo,
          languages: [],
        });
      }
    }
  }

  const handleFilterSelection = async (
    filterCategoryKey: FilterCategoryKey,
    selectedFilterValue: string
  ): Promise<void | null> => {
    const enabledFilterCategories =
      ContentFilteringService.getEnabledFilterGroups();

    if (!enabledFilterCategories) {
      return null;
    }

    const routerQuery = getRouterQueryWithSelectedFilters({
      enabledFilterCategories,
      filterCategoryKey,
      selectedFilterValue,
      filterValues,
    });

    if (!routerQuery) {
      return null;
    }

    const shallowPath = router.asPath.replace(/\?.*/, "");

    await loadFiltersOnUrl(router, shallowPath, routerQuery);
  };

  const handleTopicFilter = async (
    selectedFilterValue: string
  ): Promise<void> => {
    await handleFilterSelection(FilterCategoryKey.Topic, selectedFilterValue);
  };

  const handleTypeFilter = async (
    selectedFilterValue: string
  ): Promise<void> => {
    await handleFilterSelection(FilterCategoryKey.Type, selectedFilterValue);
  };

  const handleFrameworkFilter = async (
    selectedFilterValue: string
  ): Promise<void> => {
    await handleFilterSelection(
      FilterCategoryKey.Framework,
      selectedFilterValue
    );
  };

  const handleLanguageFilter = async (
    selectedFilterValue: string
  ): Promise<void> => {
    await handleFilterSelection(
      FilterCategoryKey.Language,
      selectedFilterValue
    );
  };

  const handleClearFilters = async (): Promise<void> => {
    clearAllFilterValues();

    const shallowPath = router.asPath.replace(/\?.*/, "");

    await router.push(
      {
        pathname: shallowPath,
      },
      undefined,
      { shallow: true }
    );
  };

  const setSearchTermFilter = (term: string) => {
    ContentFilteringService.pushSearchTerm(term);
  };

  const closeModal = () => {
    document.body.classList.remove("scroll-lock");
    ContentFilteringService.closeModal();
  };

  const openModal = () => {
    document.body.classList.add("scroll-lock");
    ContentFilteringService.openModal();
  };

  const enabledFilterGroups = ContentFilteringService.getEnabledFilterGroups();

  return {
    isModalVisible,
    enabledFilterGroups,
    results,
    hasSelectedFilters,
    availableFilterOptions,
    typeFilterValues,
    frameworkFilterValues,
    languageFilterValues,
    topicFilterValues,
    handleTypeFilter,
    handleFrameworkFilter,
    handleLanguageFilter,
    handleTopicFilter,
    handleClearFilters,
    setSearchTermFilter,
    closeModal,
    openModal,
  };
};
